import React from 'react';
import { format, formatDistanceStrict, isSameDay, addDays } from 'date-fns';
import { enUS } from 'date-fns/locale/index.js';

const formatDistanceLocale = {
	halfAMinute: '30s',
	xSeconds: '{{count}}s',
	lessThanXSeconds: '{{count}}s',
	xMinutes: '{{count}}m',
	lessThanXMinutes: '{{count}}m',
	aboutXHours: '{{count}}h',
	xHours: '{{count}}h',
	xDays: '{{count}}d',
	xWeeks: '{{count}}w',
	aboutXWeeks: '{{count}}w',
	xMonths: '{{count}}months',
	aboutXMonths: '{{count}}months',
	overXYears: '{{count}}y',
	aboutXYears: '{{count}}y',
	almostXYears: '{{count}}y',
	xYears: '{{count}}y',
};

function isNil(value: unknown) {
	return value === null || value === undefined;
}

function formatDistance(token, count) {
	return formatDistanceLocale[token].replace('{{count}}', count);
}

const BanjoContext = React.createContext({});

export function useBanjo() {
	return React.useContext(BanjoContext);
}

function transformString(value) {
	return value;
}

function transformElapsedTime(value, { getRootTime }) {
	const time = new Date(value);
	const rootTime = getRootTime();
	let distance = formatDistanceStrict(rootTime, time, {
		locale: {
			...enUS,
			formatDistance,
		},
	});

	// override "months" to "days"
	if (distance.includes('months')) {
		distance = formatDistanceStrict(rootTime, time, {
			locale: {
				...enUS,
				formatDistance,
			},
			unit: 'day',
		});
	}

	return distance;
}

function transformDateShort(value) {
	const time = new Date(value);
	const string = format(time, 'MMM d');

	return string;
}

function transformDateMediumDay(value, { getRootTime }) {
	const time = new Date(value);
	const rootTime = getRootTime();
	let string = format(time, 'EEEE, MMM d');

	const isToday = isSameDay(rootTime, time);
	const isTomorrow = isSameDay(addDays(rootTime, 1), time);

	if (isToday) {
		string = string.replace(/[^,]*/, 'Today');
	}

	if (isTomorrow) {
		string = string.replace(/[^,]*/, 'Tomorrow');
	}

	return string;
}

function transformDateMedium(value) {
	const time = new Date(value);
	const string = format(time, 'MMM d, yyyy');

	return string;
}

function transformDateMonthYear(value) {
	const time = new Date(value);
	const string = format(time, 'MMM, yyyy');

	return string;
}

function transformDateYear(value) {
	const time = new Date(value);
	const string = format(time, 'yyyy');

	return string;
}

function transformDayOfWeek(value) {
	const time = new Date(value);
	const string = format(time, 'E');

	return string;
}

function transformDayOfMonth(value) {
	const time = new Date(value);
	const string = format(time, 'd');

	return string;
}

function transformTimeLocal(value) {
	const time = new Date(value);
	const string = format(time, 'h:mm aaaa');

	return string;
}

function transformTimeLocalApprox(value) {
	const time = new Date(value);
	const string = '~' + format(time, 'h:mm aaaa');

	return string;
}

function transformCurrencyLong({ value, currency }, { locale }) {
	const string = value.toLocaleString(locale, { style: 'currency', currency });

	return string;
}

function transformCurrencyVanity({ value, currency }, { locale }) {
	const minimumFractionDigits = value === parseInt(value) ? 0 : 2;

	return value.toLocaleString(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits,
	});
}

function transformPercentLong(value, { locale }) {
	return (
		Number(value.toFixed(2)).toLocaleString(locale, {
			minimumFractionDigits: 2,
		}) + '%'
	);
}

function transformPercentShort(value, { locale }) {
	return Number(value.toFixed(0)).toLocaleString(locale) + '%';
}

function transformCurrencyChangeLong({ value, currency }, { locale }) {
	const sign = value > 0 ? '+' : '';
	return sign + value.toLocaleString(locale, { style: 'currency', currency });
}

function transformPercentChangeLong(value, { locale }) {
	const sign = value > 0 ? '+' : '';
	return (
		sign +
		(Number(value.toFixed(2)).toLocaleString(locale, {
			minimumFractionDigits: 2,
		}) +
			'%')
	);
}

function transformNumberLong(value, { locale }) {
	return Number(value.toFixed(2)).toLocaleString(locale, {
		minimumFractionDigits: 2,
	});
}

function transformInteger(value) {
	return Number(value);
}

const interpolateFunctionMap = {
	string: transformString,
	elapsedTime: transformElapsedTime,
	dateShort: transformDateShort,
	dateMediumDay: transformDateMediumDay,
	dateMedium: transformDateMedium,
	dateMonthYear: transformDateMonthYear,
	dateYear: transformDateYear,
	dayOfWeek: transformDayOfWeek,
	dayOfMonth: transformDayOfMonth,
	timeLocal: transformTimeLocal,
	timeLocalApprox: transformTimeLocalApprox,
	currencyLong: transformCurrencyLong,
	currencyVanity: transformCurrencyVanity,
	percentLong: transformPercentLong,
	percentShort: transformPercentShort,
	currencyChangeLong: transformCurrencyChangeLong,
	percentChangeLong: transformPercentChangeLong,
	numberLong: transformNumberLong,
	integer: transformInteger,
	link: null,
};

// assuming multiple variants
export function getVariant(stringObj, variants) {
	for (const variant of variants) {
		const variantObj = stringObj.default.variants[variant];

		if (variantObj) {
			return variantObj;
		}
	}

	return stringObj.default;
}

export function parseRichText(inputObject, data, config) {
	const { text, variables = {}, linkVariables = {} } = inputObject;
	const regex = /(\{\{(\w+)\}\})/g;

	return text.split(regex).reduce((acc, segment, index, segments) => {
		if (segment.startsWith('{{') && segment.endsWith('}}')) {
			const variableName = segments[index + 1];

			// Handle regular variables
			if (variables[variableName]) {
				let value = data?.[variableName];
				let wrapperFn;
				if (value?.wrapper) {
					wrapperFn = value.wrapper;
					value = value.value;
				}
				const variableType = variables[variableName].type;

				if (!interpolateFunctionMap[variableType]) {
					throw new Error(`Variable type '${variableType}' not supported.`);
				}
				if (
					isNil(value) ||
					(value.hasOwnProperty('value') && (isNil(value.value) || value.value === ''))
				) {
					if ('fallback' in variables[variableName]) {
						value = variables[variableName].fallback;
					} else {
						throw new Error(`No data or fallback for variable '${variableName}'.`);
					}
				} else {
					// Apply interpolation function
					value = interpolateFunctionMap[variableType](value, config);
				}

				return [
					...acc,
					{
						kind: 'variable',
						id: variableName,
						...variables[variableName],
						text: value,
						...(wrapperFn ? { wrapperFn } : {}),
					},
				];
			}

			// Handle link variables
			if (linkVariables[variableName]) {
				const wrapperFn = data?.[variableName]?.wrapper;
				return [
					...acc,
					{
						kind: 'link',
						id: variableName,
						...linkVariables[variableName],
						...(wrapperFn ? { wrapperFn } : {}),
					},
				];
			}

			// If variable is not found in either

			throw new Error(
				`Variable or linkVariable '${variableName}' not found in either reference objects.`
			);
		}

		if (segment && index % 3 === 0) {
			return [
				...acc,
				{
					kind: 'text',
					text: segment,
				},
			];
		}

		return acc;
	}, []);
}

function findStringObj(json, variants = [], stringId) {
	const stringObj = json.components[stringId];
	if (!stringObj) {
		console.error('Banjo: ERROR finding stringId:', stringId);
		return null;
	}

	// select text from proper variant (if string has variants)
	return stringObj.default.variants ? getVariant(stringObj, variants) : stringObj.default;
}

export function getRichTextLocale(json, variants = [], config) {
	return function getRichText(stringId, data) {
		const stringObj = findStringObj(json, variants, stringId);

		if (!stringObj) {
			return null;
		}

		return parseRichText(stringObj, data, config);
	};
}

export function getCopyLocale(json, variants = [], config) {
	return function getCopy(stringId, data, classNames, openLinksInNewWindow = false) {
		const getRichText = getRichTextLocale(json, variants, config);
		const text = getRichText(stringId, data);

		if (!text) {
			console.error(`Banjo: ERROR finding text from ${stringId}`);
		}

		return (
			<span data-testid={stringId}>
				{text?.map((textSegment, index) => {
					const className = classNames?.[textSegment.kind];
					const key = `${stringId}${index}`;
					switch (textSegment.kind) {
						case 'text':
						case 'variable': {
							const content = textSegment.wrapperFn
								? textSegment.wrapperFn({ value: textSegment.text })
								: textSegment.text;
							return className ? (
								<span key={key} className={className}>
									{content}
								</span>
							) : textSegment.wrapperFn ? (
								<React.Fragment key={key}>{content}</React.Fragment>
							) : (
								content
							);
						}

						case 'link': {
							const content = textSegment.wrapperFn
								? textSegment.wrapperFn({ value: textSegment.text, url: textSegment.url })
								: textSegment.text;
							return textSegment.wrapperFn ? (
								className ? (
									<span key={key} className={className}>
										{content}
									</span>
								) : (
									<React.Fragment key={key}>{content}</React.Fragment>
								)
							) : (
								<a
									key={key}
									className={className}
									href={textSegment.url}
									data-testid={key}
									rel="noopener noreferrer"
									target={openLinksInNewWindow ? "_blank" : "_self"}
								>
									{textSegment.text}
								</a>
							);
						}

						default: {
							console.error(
								`Banjo: ERROR finding textSegment(${textSegment.kind}) from ${stringId}`
							);
							return null;
						}
					}
				})}
			</span>
		);
	};
}

export function getStringLocale(json, variants = [], config) {
	return function getString(stringId, data) {
		const getRichText = getRichTextLocale(json, variants, config);
		const text = getRichText(stringId, data);

		if (!text) {
			console.error(`Banjo: ERROR finding text from ${stringId}`);
		}

		return text
			?.map((textSegment) => {
				switch (textSegment.kind) {
					case 'text':
					case 'variable': {
						return textSegment.text;
					}
					case 'link': {
						console.error(
							`Banjo: ERROR Cannot textSegment(${textSegment.kind}) from ${stringId} as a string`
						);
						return null;
					}
					default: {
						console.error(
							`Banjo: ERROR finding textSegment(${textSegment.kind}) from ${stringId}`
						);
						return null;
					}
				}
			})
			.join('');
	};
}

const getRootTime = () => new Date();

export function BanjoProvider(props) {
	const [locale, setLocale] = React.useState(props.defaultLocale);
	const localizedJson = props.languageMap[locale];

	const getCopy = getCopyLocale(localizedJson, props.variants, {
		locale,
		getRootTime,
	});

	const getRichText = getRichTextLocale(localizedJson, props.variants, {
		locale,
		getRootTime,
	});

	const getString = getStringLocale(localizedJson, props.variants, {
		locale,
		getRootTime,
	});

	return (
		<BanjoContext.Provider
			value={{
				getCopy,
				getRichText,
				getString,
				localizedJson,
				locale,
				setLocale,
				version: localizedJson.version,
			}}
		>
			{props.children}
		</BanjoContext.Provider>
	);
}

BanjoProvider.defaultProps = {
	defaultLocale: 'en-us',
};

export const withBanjoContext = (Component) => (props) =>
	(
		<BanjoContext.Consumer>
			{(contextProps) => <Component {...contextProps} {...props} />}
		</BanjoContext.Consumer>
	);

export function BanjoString(props) {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore-next-line
	const text = useBanjo().getCopy(props.stringId, props.data, props.classNames, props.openLinksInNewWindow) ?? null;
	return text;
}
